// Schedule › To schedule, Blueprint only (pages/ux-full inbox/tobook; BL-189), on ListPage: the work
// waiting for someone here. On the left, customers asking to move a visit (over the list) and the
// estimates sold but not booked (the list); on the right the membership visits due in the next 30 days
// (beside it), with the leads and Find a lead under them. Each list is a RowList; clearing a hold is a
// ConfirmDialog that asks what the customer said, booking a membership visit a FormDialog. Riverstone's
// To book stays ToBook.tsx, with the same parts' hooks (use-visit-requests, use-leads, use-lead-search).
// While the company mirrors its old system, sold work and membership visits are booked there, and
// holds cleared there: listed here to see (BL-088). Leads stay ours.
import { useState } from 'react';
import { RpcError } from '@flowology/client';
import { ConfirmDialog, FormDialog, Notice, RowList, SelectField, type Row } from '@flowology/ui-web/fl';
import { ListPage } from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { DAY_WINDOWS, dayOptions, today as todayIn, windowLabel, zonedInstant } from '../book-model.ts';
import { useQuery } from '../books/ui.tsx';
import { moneyRights } from '../history/money-model.ts';
import { DISPATCH_PERMS } from '../job/job-model.ts';
import { openJob } from '../job/job-page.ts';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { money } from '../review/review-model.ts';
import { MIRROR_LINE, useMirror } from '../shared/mirror.ts';
import { useSaveKey } from '../shared/save-key.ts';
import { AskedToMove } from './AskedToMove.tsx';
import { FindLead } from './FindLead.tsx';
import { LeadsPart } from './LeadsPart.tsx';
import { SoldDeposits } from './SoldDeposits.tsx';
import { listStatus, rowTone } from './to-schedule-model.ts';
import {
  dueHorizon,
  dueWords,
  soldState,
  type DueVisit,
  type Lead,
  type SoldEstimate,
} from './tobook-model.ts';
import './to-schedule.css';

export const TO_SCHEDULE_LEDE =
  'Work waiting for someone here: customers asking to move a visit, sold estimates, membership visits due, and leads.';

const message = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'Something went wrong.';
const longDate = (d: string) =>
  new Intl.DateTimeFormat('en-CA', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${d}T12:00:00Z`));

export function ToSchedulePage({
  nav,
  timeZone,
  onBook,
  onBookLead,
  onChanged,
}: {
  nav: NavProps;
  timeZone: string;
  onBook: (estimate: SoldEstimate) => void;
  onBookLead: (lead: Lead) => void;
  /** Something here changed what waits (a lead added or closed): the Inbox count re-reads. */
  onChanged: () => void;
}) {
  const today = todayIn(timeZone);
  const crumb = usePageCrumb(nav);
  const mirroring = useMirror();
  const held = nav.permissions ?? [];
  const sold = useQuery<{ estimates: SoldEstimate[] }>('estimates.soldNotBooked', {});
  const due = useQuery<{ due: DueVisit[] }>('recurringServices.due', { asOf: dueHorizon(today) });
  const [toast, setToast] = useState<string | null>(null);
  const [picking, setPicking] = useState<DueVisit | null>(null);
  // A held or cancel-asked estimate being cleared, once someone has spoken with the customer (QA-576)
  const [clearing, setClearing] = useState<SoldEstimate | null>(null);
  const estimates = sold.data?.estimates ?? null;
  const visits = due.data?.due ?? null;
  const clearHold = clearing ? soldState(clearing).clear === 'hold' : false;

  return (
    <div className="app">
      <main className="bp-tobook-main">
        <PageBanners nav={nav} />
        <ListPage
          // Blueprint calls the page what its rail does (pages/ux-full inbox/tobook)
          head={{ ...(crumb ? { crumb } : {}), title: 'To schedule', lede: TO_SCHEDULE_LEDE }}
          notice={toast ? <Notice tone="done" title={toast} /> : null}
          list={{
            title: 'Sold, not booked',
            ...(estimates ? { count: estimates.length } : {}),
            what: 'the sold estimates',
            status: listStatus(estimates, sold.error),
            emptyText: 'Nothing waiting: every sold estimate is booked.',
            failedText: sold.error ?? 'The sold estimates didn’t load.',
            onRetry: () => void sold.reload(),
            before: (
              <AskedToMove
                timeZone={timeZone}
                canMove={DISPATCH_PERMS.some((p) => held.includes(p))}
                onChanged={onChanged}
              />
            ),
            children: (
              <>
                {mirroring && <p className="bp-tobook-sub">{MIRROR_LINE}</p>}
                <RowList
                  label="Sold, not booked"
                  rows={(estimates ?? []).map((e): Row => {
                    const st = soldState(e);
                    return {
                      id: e.estimateId,
                      name: e.customerName,
                      // the row opens its job (one action a row: Book it, or clearing what holds it)
                      onOpen: () => openJob(e.jobId),
                      openLabel: `${e.customerName}: open job #${e.jobNumber}`,
                      end: { figure: money(e.totalCents) },
                      lines: [
                        {
                          text: `Estimate #${e.number} · ${e.optionTitle} · from job #${e.jobNumber} · ${new Intl.DateTimeFormat(
                            'en-CA',
                            { timeZone, dateStyle: 'medium' },
                          ).format(new Date(e.soldAt))}`,
                        },
                        { word: st.chip, tone: rowTone(st.tone) },
                        ...(st.why ? [{ text: st.why, tone: 'ink' as const, size: 'sm' as const }] : []),
                        {
                          part: (
                            <SoldDeposits
                              estimateId={e.estimateId}
                              estimateNumber={e.number}
                              customerName={e.customerName}
                              totalCents={e.totalCents}
                              mayTake={moneyRights(held, mirroring).record}
                              onTaken={setToast}
                            />
                          ),
                        },
                      ],
                      // what can be done first: Book it, or clearing a hold or a request that stops it
                      actions: mirroring
                        ? []
                        : [
                            ...(st.bookable || !st.clear
                              ? [
                                  {
                                    id: 'book',
                                    text: 'Book it',
                                    isDisabled: !st.bookable,
                                    onPress: () => onBook(e),
                                    label: `Book estimate ${e.number} for ${e.customerName}`,
                                  },
                                ]
                              : []),
                            ...(st.clear
                              ? [
                                  {
                                    id: 'clear',
                                    text: st.clear === 'hold' ? 'Clear the hold' : 'Clear their request',
                                    onPress: () => setClearing(e),
                                  },
                                ]
                              : []),
                          ],
                    };
                  })}
                />
              </>
            ),
          }}
          aside={{
            title: 'Membership visits due in the next 30 days',
            ...(visits ? { count: visits.length } : {}),
            what: 'the membership visits',
            status: listStatus(visits, due.error),
            emptyText: 'No membership visits due.',
            failedText: due.error ?? 'The membership visits didn’t load.',
            onRetry: () => void due.reload(),
            children: (
              <RowList
                label="Membership visits due"
                rows={(visits ?? []).map((d): Row => {
                  const w = dueWords(d.dueOn, today);
                  return {
                    id: d.recurringServiceId,
                    name: d.customerName,
                    end: { word: `${w.text} (${longDate(d.dueOn)})`, tone: rowTone(w.tone) },
                    lines: [
                      {
                        text: `${d.address} · ${d.membershipName}: ${d.jobTypeName} · ${d.visitsLeft} visit${
                          d.visitsLeft === 1 ? '' : 's'
                        } left · ends ${longDate(d.endsOn)}`,
                      },
                      ...(!mirroring && !d.businessUnitId
                        ? [
                            {
                              text: `The ${d.jobTypeName} job type has no department yet, so this can’t be booked until it has one.`,
                              tone: 'ink' as const,
                              size: 'sm' as const,
                            },
                          ]
                        : []),
                    ],
                    actions:
                      !mirroring && d.businessUnitId
                        ? [{ id: 'book', text: 'Book visit', onPress: () => setPicking(d) }]
                        : [],
                  };
                })}
              />
            ),
            after: (
              <>
                <LeadsPart timeZone={timeZone} onBook={onBookLead} onChanged={onChanged} />
                {/* any lead, found by its name, phone, email or words, with its ad click (BL-092) */}
                <FindLead timeZone={timeZone} />
              </>
            ),
          }}
          layers={
            <>
              <ConfirmDialog
                isOpen={!!clearing}
                onClose={() => setClearing(null)}
                title={clearHold ? 'Clear the hold' : 'Clear their request to cancel'}
                confirmLabel={clearHold ? 'Clear the hold' : 'Clear their request'}
                cancelLabel="Cancel"
                reason={{ label: 'What they told you', missing: 'Say what the customer told you.' }}
                onConfirm={async (said) => {
                  if (!clearing) return;
                  try {
                    await client.call('estimates.clearHold', {
                      estimateId: clearing.estimateId,
                      clear: clearHold ? 'hold' : 'cancel_request',
                      said,
                    });
                  } catch (e) {
                    throw new Error(message(e), { cause: e });
                  }
                  setToast(
                    `Cleared. ${clearing.customerName}’s estimate #${clearing.number} can be booked now.`,
                  );
                  void sold.reload();
                }}
              >
                {clearing
                  ? clearHold
                    ? `${clearing.customerName} said the signature on estimate #${clearing.number} was wrong. Clear it only once you’ve spoken with them and they want the work. If they want it cancelled, cancel the estimate instead.`
                    : `${clearing.customerName} asked to cancel estimate #${clearing.number}. Clear it only if, when you called, they still want the work. If they still want it cancelled, cancel the estimate instead.`
                  : null}
              </ConfirmDialog>
              <VisitDialog
                visit={picking}
                today={today}
                timeZone={timeZone}
                onClose={() => setPicking(null)}
                onBooked={(v, n) => {
                  setToast(`Booked job ${n} for ${v.customerName}. Assign the technician on the board.`);
                  void due.reload();
                }}
              />
            </>
          }
        />
      </main>
    </div>
  );
}

/** Book a membership visit that's due: a day in the next two weeks and an arrival window. */
function VisitDialog({
  visit,
  today,
  timeZone,
  onClose,
  onBooked,
}: {
  visit: DueVisit | null;
  today: string;
  timeZone: string;
  onClose: () => void;
  onBooked: (visit: DueVisit, jobNumber: string) => void;
}) {
  const days = dayOptions(today, 14);
  const [date, setDate] = useState(days[1]!.date);
  const [win, setWin] = useState(0);
  const [opened, setOpened] = useState<string | null>(null);
  if ((visit?.recurringServiceId ?? null) !== opened) {
    setOpened(visit?.recurringServiceId ?? null);
    setDate(days[1]!.date);
    setWin(0);
  }
  // The same day and window, booked again after a lost answer, keep the key: never booked twice.
  const save = useSaveKey(`${visit?.recurringServiceId}:${date}:${win}`);
  return (
    <FormDialog
      isOpen={!!visit}
      onClose={onClose}
      title={visit ? `Book ${visit.customerName}’s visit` : ''}
      submitLabel="Book visit"
      isDirty={date !== days[1]!.date || win !== 0}
      note={
        visit
          ? `${visit.membershipName}: ${visit.jobTypeName} at ${visit.address}. Assign the technician on the board.`
          : null
      }
      failText="Couldn’t book the visit. Try again; it won’t be booked twice."
      onSubmit={async () => {
        if (!visit) return;
        const [s, e] = DAY_WINDOWS[win]!;
        let r: { jobNumber: string };
        try {
          r = await client.call<{ jobNumber: string }>(
            'memberships.bookVisit',
            {
              recurringServiceId: visit.recurringServiceId,
              businessUnitId: visit.businessUnitId!,
              start: zonedInstant(date, s, timeZone),
              end: zonedInstant(date, e, timeZone),
            },
            { idempotencyKey: save.key },
          );
        } catch (err) {
          const lost = !(err instanceof RpcError) || err.code === 'offline' || err.code === 'internal';
          throw new Error(
            lost ? 'Couldn’t book the visit. Try again; it won’t be booked twice.' : message(err),
            {
              cause: err,
            },
          );
        }
        onBooked(visit, r.jobNumber);
      }}
    >
      <SelectField
        label="Day"
        value={date}
        onChange={setDate}
        options={days.map((d) => ({ value: d.date, label: d.label }))}
      />
      <SelectField
        label="Arrival window"
        value={String(win)}
        onChange={(v) => setWin(Number(v))}
        options={DAY_WINDOWS.map((w, i) => ({ value: String(i), label: windowLabel(w) }))}
      />
    </FormDialog>
  );
}
